/*!
 * Copyright (c) Microsoft Corporation and contributors. All rights reserved.
 * Licensed under the MIT License.
 */
import { UsageError } from "@fluidframework/telemetry-utils/internal";
import { normalizeFieldSchema, FieldKind, normalizeAllowedTypes, } from "../schemaTypes.js";
import { NodeKind } from "../core/index.js";
import { toFlexSchema } from "../toFlexSchema.js";
import { LeafNodeSchema } from "../leafNodeSchema.js";
import { assert } from "@fluidframework/core-utils/internal";
import { isObjectNodeSchema } from "../objectNodeTypes.js";
import { markSchemaMostDerived } from "./schemaFactory.js";
import { fail, getOrCreate } from "../../util/index.js";
const defaultTreeConfigurationOptions = {
    enableSchemaValidation: false,
    preventAmbiguity: false,
};
/**
 * Configuration for {@link ITree.viewWith}.
 * @sealed @public
 */
export class TreeViewConfiguration {
    /**
     * @param props - Property bag of configuration options.
     */
    constructor(props) {
        const config = { ...defaultTreeConfigurationOptions, ...props };
        this.schema = config.schema;
        this.enableSchemaValidation = config.enableSchemaValidation;
        this.preventAmbiguity = config.preventAmbiguity;
        // Ambiguity errors are lower priority to report than invalid schema errors, so collect these in an array and report them all at once.
        const ambiguityErrors = [];
        walkFieldSchema(config.schema, {
            // Ensure all reachable schema are marked as most derived.
            // This ensures if multiple schema extending the same schema factory generated class are present (or have been constructed, or get constructed in the future),
            // an error is reported.
            node: markSchemaMostDerived,
            allowedTypes(types) {
                if (config.preventAmbiguity) {
                    checkUnion(types, ambiguityErrors);
                }
            },
        });
        if (ambiguityErrors.length !== 0) {
            // Duplicate errors are common since when two types conflict, both orders error:
            const deduplicated = new Set(ambiguityErrors);
            throw new UsageError(`Ambigious schema found:\n${[...deduplicated].join("\n")}`);
        }
        // Eagerly perform this conversion to surface errors sooner.
        toFlexSchema(config.schema);
    }
}
/**
 * Pretty print a set of types for use in error messages.
 */
function formatTypes(allowed) {
    // Use JSON.stringify to quote and escape identifiers.
    // Don't just use a single array JSON.stringify since that omits spaces between items
    return `[${Array.from(allowed, (s) => JSON.stringify(s.identifier)).join(", ")}]`;
}
/**
 * Detect cases documented in {@link ITreeConfigurationOptions.preventAmbiguity}.
 */
export function checkUnion(union, errors) {
    const checked = new Set();
    const maps = [];
    const arrays = [];
    const objects = [];
    // Map from key to schema using that key
    const allObjectKeys = new Map();
    for (const schema of union) {
        if (checked.has(schema)) {
            throw new UsageError(`Duplicate schema in allowed types: ${schema.identifier}`);
        }
        checked.add(schema);
        if (schema instanceof LeafNodeSchema) {
            // nothing to do
        }
        else if (isObjectNodeSchema(schema)) {
            objects.push(schema);
            for (const key of schema.fields.keys()) {
                getOrCreate(allObjectKeys, key, () => new Set()).add(schema);
            }
        }
        else if (schema.kind === NodeKind.Array) {
            arrays.push(schema);
        }
        else {
            assert(schema.kind === NodeKind.Map, 0x9e7 /* invalid schema */);
            maps.push(schema);
        }
    }
    if (arrays.length > 1) {
        errors.push(`More than one kind of array allowed within union (${formatTypes(arrays)}). This would require type disambiguation which is not supported by arrays during import or export.`);
    }
    if (maps.length > 1) {
        errors.push(`More than one kind of map allowed within union (${formatTypes(maps)}). This would require type disambiguation which is not supported by maps during import or export.`);
    }
    if (maps.length > 0 && arrays.length > 0) {
        errors.push(`Both a map and an array allowed within union (${formatTypes([...arrays, ...maps])}). Both can be implicitly constructed from iterables like arrays, which are ambiguous when the array is empty.`);
    }
    if (objects.length > 0 && maps.length > 0) {
        errors.push(`Both a object and a map allowed within union (${formatTypes([...objects, ...maps])}). Both can be constructed from objects and can be ambiguous.`);
    }
    // Check for objects which fully overlap:
    for (const schema of objects) {
        // All objects which might be ambiguous relative to `schema`.
        const possiblyAmbiguous = new Set(objects);
        // A schema can't be ambiguous with itself
        possiblyAmbiguous.delete(schema);
        // For each field of schema, remove schema from possiblyAmbiguous that do not have that field
        for (const [key, field] of schema.fields) {
            if (field.kind === FieldKind.Required) {
                const withKey = allObjectKeys.get(key) ?? fail("missing schema");
                for (const candidate of possiblyAmbiguous) {
                    if (!withKey.has(candidate)) {
                        possiblyAmbiguous.delete(candidate);
                    }
                }
            }
        }
        if (possiblyAmbiguous.size > 0) {
            // TODO: make this check more permissive.
            // Allow using the type of the field to disambiguate, at least for leaf types.
            // Add "constant" fields which can be used to disambiguate even more cases without adding persisted data: maybe make them optional in constructor?
            // Consider separating unambiguous implicit construction format from constructor arguments at type level, allowing constructor to superset the implicit construction options (ex: optional constant fields).
            // The policy here however must remain at least as conservative as shallowCompatibilityTest in src/simple-tree/toMapTree.ts.
            errors.push(`The required fields of ${JSON.stringify(schema.identifier)} are insufficient to differentiate it from the following types: ${formatTypes(possiblyAmbiguous)}. For objects to be considered unambiguous, each must have required fields that do not all occur on any other object in the union.`);
        }
    }
}
export function walkNodeSchema(schema, visitor, visitedSet) {
    if (visitedSet.has(schema)) {
        return;
    }
    visitedSet.add(schema);
    if (schema instanceof LeafNodeSchema) {
        // nothing to do
    }
    else if (isObjectNodeSchema(schema)) {
        for (const field of schema.fields.values()) {
            walkFieldSchema(field, visitor, visitedSet);
        }
    }
    else {
        assert(schema.kind === NodeKind.Array || schema.kind === NodeKind.Map, 0x9b3 /* invalid schema */);
        const childTypes = schema.info;
        walkAllowedTypes(normalizeAllowedTypes(childTypes), visitor, visitedSet);
    }
    // This visit is done at the end so the traversal order is most inner types first.
    // This was picked since when fixing errors,
    // working from the inner types out to the types that use them will probably go better than the reverse.
    // This does not however ensure all types referenced by a type are visited before it, since in recursive cases thats impossible.
    visitor.node?.(schema);
}
export function walkFieldSchema(schema, visitor, visitedSet = new Set()) {
    walkAllowedTypes(normalizeFieldSchema(schema).allowedTypeSet, visitor, visitedSet);
}
export function walkAllowedTypes(allowedTypes, visitor, visitedSet) {
    for (const childType of allowedTypes) {
        walkNodeSchema(childType, visitor, visitedSet);
    }
    visitor.allowedTypes?.(allowedTypes);
}
//# sourceMappingURL=tree.js.map